import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  Users, UserPlus, Search, ShieldCheck, ShieldAlert, Building2,
  RefreshCw, Trash2, Edit3, Mail, Phone, Calendar, CheckCircle2,
  Lock, AlertCircle
} from "lucide-react";

export interface UserRecord {
  id: string;
  email: string;
  name: string;
  role: "super_admin" | "company_admin" | "ca" | "accountant";
  company_id: string | null;
  company_name?: string | null;
  phone?: string | null;
  created_at: string;
  updated_at?: string;
}

const ROLE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  super_admin: { bg: "bg-purple-500/10", text: "text-purple-600 dark:text-purple-400", border: "border-purple-500/20" },
  company_admin: { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400", border: "border-blue-500/20" },
  ca: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", border: "border-amber-500/20" },
  accountant: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-500/20" },
};

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  ca: "Chartered Accountant",
  accountant: "Accountant",
};

export default function UsersPage() {
  const { user: authUser, role: currentRole } = useAuth();
  const { companies } = useCompany();
  const { toast } = useToast();

  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [deletingUser, setDeletingUser] = useState<UserRecord | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states for creating a new user
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "company_admin" as UserRecord["role"],
    company_id: "",
    phone: "",
  });

  // Load users from backend
  const fetchUsers = useCallback(async () => {
    try {
      if (currentRole === "super_admin") {
        const token = localStorage.getItem("accoupro_auth_session")
          ? JSON.parse(localStorage.getItem("accoupro_auth_session")!).access_token
          : "";

        const res = await fetch("/api/admin/users", {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "x-user-id": authUser?.id || "",
          },
        });

        if (res.ok) {
          const json = await res.json();
          if (json.success && Array.isArray(json.data)) {
            setUsers(json.data);
            return;
          }
        }
      }

      // Fallback or company-scoped query
      if (currentRole === "super_admin") {
        const { data: dbUsers, error } = await supabase
          .from("users")
          .select("id, email, name, role, company_id, phone, created_at, updated_at")
          .order("created_at", { ascending: false });

        if (!error && Array.isArray(dbUsers)) {
          const compMap = new Map<string, string>();
          companies.forEach((c) => compMap.set(c.id, c.name));

          const enriched = dbUsers.map((u: any) => ({
            ...u,
            company_name: u.company_id ? compMap.get(u.company_id) || "Company (" + u.company_id.slice(0, 8) + ")" : null,
          }));
          setUsers(enriched);
        }
      } else {
        // Scoped to active company for company admins
        const targetCompId = authUser?.company || (authUser as any)?.company_id;
        if (targetCompId) {
          const { data: members, error } = await supabase
            .from("company_members")
            .select("*")
            .eq("company_id", targetCompId);

          if (!error && Array.isArray(members)) {
            const mapped: UserRecord[] = members.map((m: any) => ({
              id: m.user_id,
              name: m.user_name || "Team Member",
              email: m.email,
              role: m.role || "accountant",
              company_id: targetCompId,
              company_name: companies.find((c) => c.id === targetCompId)?.name || "Current Company",
              created_at: m.joined_at,
            }));
            setUsers(mapped);
          }
        } else {
          setUsers([]);
        }
      }
    } catch (err: any) {
      console.error("[UsersPage] Error fetching users:", err);
      toast({
        title: "Error loading users",
        description: err.message || "Failed to load user records from database",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentRole, authUser, companies, toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchUsers();
  };

  // Create new user handler (Super Admin)
  const handleCreateUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password) {
      toast({ title: "Validation Error", description: "Name, email, and password are required", variant: "destructive" });
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem("accoupro_auth_session")
        ? JSON.parse(localStorage.getItem("accoupro_auth_session")!).access_token
        : "";

      const res = await fetch("/api/admin/users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "x-user-id": authUser?.id || "",
        },
        body: JSON.stringify({
          name: newUser.name.trim(),
          email: newUser.email.trim().toLowerCase(),
          password: newUser.password,
          role: newUser.role,
          company_id: newUser.company_id || null,
          phone: newUser.phone.trim() || null,
        }),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "Failed to create user");
      }

      toast({ title: "User Created", description: `Account for ${newUser.name} created successfully` });
      setShowCreateDialog(false);
      setNewUser({ name: "", email: "", password: "", role: "company_admin", company_id: "", phone: "" });
      fetchUsers();
    } catch (err: any) {
      toast({ title: "Creation Failed", description: err.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  // Update existing user handler (Super Admin)
  const handleUpdateUser = async () => {
    if (!editingUser) return;
    setActionLoading(true);

    try {
      const token = localStorage.getItem("accoupro_auth_session")
        ? JSON.parse(localStorage.getItem("accoupro_auth_session")!).access_token
        : "";

      const res = await fetch(`/api/admin/users/${editingUser.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "x-user-id": authUser?.id || "",
        },
        body: JSON.stringify({
          name: editingUser.name,
          role: editingUser.role,
          company_id: editingUser.company_id || null,
          phone: editingUser.phone || null,
        }),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "Failed to update user");
      }

      toast({ title: "User Updated", description: `${editingUser.name}'s details saved` });
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      toast({ title: "Update Failed", description: err.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  // Delete user handler (Super Admin)
  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    if (deletingUser.id === authUser?.id) {
      toast({ title: "Action Prohibited", description: "You cannot delete your own logged-in account", variant: "destructive" });
      setDeletingUser(null);
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem("accoupro_auth_session")
        ? JSON.parse(localStorage.getItem("accoupro_auth_session")!).access_token
        : "";

      const res = await fetch(`/api/admin/users/${deletingUser.id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "x-user-id": authUser?.id || "",
        },
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || "Failed to delete user");
      }

      toast({ title: "User Removed", description: `${deletingUser.name} (${deletingUser.email}) removed from system` });
      setDeletingUser(null);
      fetchUsers();
    } catch (err: any) {
      toast({ title: "Deletion Failed", description: err.message, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchesSearch =
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.company_name && u.company_name.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesRole = roleFilter === "all" || u.role === roleFilter;

      return matchesSearch && matchesRole;
    });
  }, [users, searchQuery, roleFilter]);

  // Statistics
  const stats = useMemo(() => {
    return {
      total: users.length,
      superAdmins: users.filter((u) => u.role === "super_admin").length,
      companyAdmins: users.filter((u) => u.role === "company_admin").length,
      staff: users.filter((u) => u.role === "ca" || u.role === "accountant").length,
    };
  }, [users]);

  const formatDate = (isoString: string) => {
    if (!isoString) return "—";
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return isoString;
    }
  };

  const getInitials = (name: string) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold font-display text-foreground tracking-tight">Users & Access</h1>
            <Badge variant="outline" className="text-xs bg-primary/5 text-primary border-primary/20">
              {currentRole === "super_admin" ? "Platform-Wide" : "Company Team"}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {currentRole === "super_admin"
              ? "All registered users, role privileges, and company associations across AccouPro."
              : "Manage members, accountants, and Chartered Accountants for your company."}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="gap-1.5">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>

          {currentRole === "super_admin" && (
            <Button size="sm" onClick={() => setShowCreateDialog(true)} className="gap-1.5 shadow-soft">
              <UserPlus className="h-4 w-4" /> Add User
            </Button>
          )}
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider">Total Users</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.total}</div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Legitimate platform accounts</p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider text-purple-600 dark:text-purple-400">
              Super Admins
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.superAdmins}</div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Platform administrators</p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider text-blue-600 dark:text-blue-400">
              Company Admins
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.companyAdmins}</div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Business workspace owners</p>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              CAs & Accountants
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.staff}</div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Financial & audit team</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-card border-border"
          />
        </div>

        <div className="flex items-center gap-2">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px] bg-card border-border text-xs">
              <SelectValue placeholder="Filter by Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="super_admin">Super Admins</SelectItem>
              <SelectItem value="company_admin">Company Admins</SelectItem>
              <SelectItem value="ca">Chartered Accountants</SelectItem>
              <SelectItem value="accountant">Accountants</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users Table */}
      <Card className="border-border overflow-hidden">
        {loading ? (
          <div className="py-16 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading registered users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-16 text-center px-4">
            <div className="w-12 h-12 rounded-xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto mb-3">
              <Users className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold text-foreground">No Users Found</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
              {searchQuery || roleFilter !== "all"
                ? "No registered users match your search criteria. Try resetting the filters."
                : "No users registered in the database yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead className="w-[260px]">User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead>Status</TableHead>
                  {currentRole === "super_admin" && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => {
                  const roleStyle = ROLE_COLORS[u.role] || { bg: "bg-muted", text: "text-muted-foreground", border: "border-border" };
                  const isSelf = authUser?.id === u.id;

                  return (
                    <TableRow key={u.id} className="hover:bg-muted/30">
                      {/* User Column */}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0 border border-primary/20">
                            {getInitials(u.name)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-sm text-foreground truncate">{u.name}</span>
                              {isSelf && (
                                <Badge variant="secondary" className="text-[10px] py-0 px-1.5 h-4">
                                  You
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                              <Mail className="h-3 w-3 shrink-0" />
                              <span className="truncate">{u.email}</span>
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      {/* Role Column */}
                      <TableCell>
                        <Badge variant="outline" className={`text-xs capitalize font-medium ${roleStyle.bg} ${roleStyle.text} ${roleStyle.border}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </Badge>
                      </TableCell>

                      {/* Company Column */}
                      <TableCell>
                        {u.company_name ? (
                          <div className="flex items-center gap-1.5 text-xs text-foreground font-medium">
                            <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
                            <span className="truncate">{u.company_name}</span>
                          </div>
                        ) : u.company_id ? (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span>{u.company_id}</span>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground border-dashed">
                            Unassigned
                          </Badge>
                        )}
                      </TableCell>

                      {/* Registered Date Column */}
                      <TableCell>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3 shrink-0" />
                          <span>{formatDate(u.created_at)}</span>
                        </div>
                      </TableCell>

                      {/* Status Column */}
                      <TableCell>
                        <Badge variant="outline" className="text-[11px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20 font-medium gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Active
                        </Badge>
                      </TableCell>

                      {/* Actions Column (Super Admin) */}
                      {currentRole === "super_admin" && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={() => setEditingUser(u)}
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            {!isSelf && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                onClick={() => setDeletingUser(u)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Dialog: Create User (Super Admin) */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" /> Create New User
            </DialogTitle>
            <DialogDescription>
              Register a genuine user account directly into the database.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3.5 py-2">
            <div>
              <Label htmlFor="create-name">Full Name *</Label>
              <Input
                id="create-name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="e.g. Gurveer Singh"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="create-email">Email Address *</Label>
              <Input
                id="create-email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="e.g. user@hsecurities.in"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="create-password">Initial Password *</Label>
              <Input
                id="create-password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="••••••••"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="create-role">Role</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(v) => setNewUser({ ...newUser, role: v as UserRecord["role"] })}
                >
                  <SelectTrigger id="create-role" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company_admin">Company Admin</SelectItem>
                    <SelectItem value="ca">Chartered Accountant</SelectItem>
                    <SelectItem value="accountant">Accountant</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="create-phone">Phone (Optional)</Label>
                <Input
                  id="create-phone"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder="+91 98765 43210"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="create-company">Assign Company</Label>
              <Select
                value={newUser.company_id}
                onValueChange={(v) => setNewUser({ ...newUser, company_id: v === "none" ? "" : v })}
              >
                <SelectTrigger id="create-company" className="mt-1">
                  <SelectValue placeholder="Select company or unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Company (Unassigned)</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={actionLoading} className="gap-2">
              {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit User (Super Admin) */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-primary" /> Edit User: {editingUser?.name}
            </DialogTitle>
            <DialogDescription>
              Update role, company association, or contact details for this account.
            </DialogDescription>
          </DialogHeader>

          {editingUser && (
            <div className="space-y-3.5 py-2">
              <div>
                <Label>Email</Label>
                <Input value={editingUser.email} disabled className="mt-1 bg-muted/50 cursor-not-allowed" />
              </div>

              <div>
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-role">Role</Label>
                  <Select
                    value={editingUser.role}
                    onValueChange={(v) => setEditingUser({ ...editingUser, role: v as UserRecord["role"] })}
                  >
                    <SelectTrigger id="edit-role" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company_admin">Company Admin</SelectItem>
                      <SelectItem value="ca">Chartered Accountant</SelectItem>
                      <SelectItem value="accountant">Accountant</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={editingUser.phone || ""}
                    onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                    placeholder="+91..."
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit-company">Assign Company</Label>
                <Select
                  value={editingUser.company_id || "none"}
                  onValueChange={(v) => setEditingUser({ ...editingUser, company_id: v === "none" ? null : v })}
                >
                  <SelectTrigger id="edit-company" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Company (Unassigned)</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser} disabled={actionLoading}>
              {actionLoading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete Confirmation */}
      <Dialog open={!!deletingUser} onOpenChange={() => setDeletingUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Confirm Account Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete user <strong>{deletingUser?.name}</strong> ({deletingUser?.email})? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingUser(null)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={actionLoading}>
              {actionLoading ? "Deleting..." : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
